import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// API base: backend (e.g. WSL) on port 8000; override with VITE_API_URL if set
const API_BASE = String(import.meta?.env?.VITE_API_URL ?? 'https://jabil-backend.aws-practice.royalcyber.org').replace(/\/$/, '')
const LIVE_LOGS_URL = `${API_BASE}/jobs/live`

interface LogEvent {
  source: string
  message: string
  timestamp?: string
  eventId?: string
}

interface LiveConnected {
  type: 'connected'
  sources: string[]
  window_sec: number
  poll_interval_sec: number
}

interface LiveStatus {
  type: 'status'
  at: string
  window_start_ms: number
  window_end_ms: number
  sources: Record<string, { events: number; error: string | null }>
}

interface LiveLogEvent {
  source: string
  logGroup?: string
  logStream?: string
  timestamp?: string
  message: string
  eventId?: string
}

interface UploadTabProps {
  onFileUpload: (file: File, config?: any) => Promise<string>
  onTokenReceived: (token: string) => void
  onTabChange: () => void
  jobToken?: string
  jobStatus?: string
  jobProgress?: number
  jobTotalRows?: number
}

const UploadTab: React.FC<UploadTabProps> = ({ onFileUpload, onTokenReceived, onTabChange, jobToken, jobStatus, jobProgress = 0, jobTotalRows = 0 }) => {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null)
  const [showConfig, setShowConfig] = useState(false)
  const [partLimit, setPartLimit] = useState(5)
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  const [newEmailInput, setNewEmailInput] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [logLines, setLogLines] = useState<LogEvent[]>([])
  const [liveConnected, setLiveConnected] = useState<LiveConnected | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const seenEventIds = useRef<Set<string>>(new Set())
  const liveEventSourceRef = useRef<EventSource | null>(null)

  // Strip timestamp/logger prefix for cleaner step display (e.g. "2026-02-06 03:25:07 - __main__ - INFO - Job ..." -> "Job ...")
  const formatLogForStep = (raw: string) => {
    return raw
      .replace(/^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}:\d{2}([.,]\d+)?(Z)?\s*(-\s*[\w.]+\s*-\s*\w+\s*-\s*)?/i, '')
      .trim() || raw
  }

  // Steps driven by live log stream (batch-worker, orchestrator, supplier)
  const STEPS = [
    { title: 'File received', icon: '📤', desc: 'Shortage file received – job queued' },
    { title: 'Batch worker started', icon: '⚡', desc: 'Worker picked up job – initializing pipeline' },
    { title: 'Job running', icon: '🔄', desc: 'Job moved to processing – MRP and site mappings' },
    { title: 'AML lookup', icon: '🔐', desc: 'Fetching AML – approved manufacturers per part' },
    { title: 'AML results', icon: '📋', desc: 'JPNs matched in AML – enriching part data' },
    { title: 'Processing parts', icon: '🎯', desc: 'Processing parts for this job – building analysis set' },
    { title: 'Orchestrator', icon: '⚖️', desc: 'Orchestrator calling supplier agent & inventory' },
    { title: 'Supplier APIs', icon: '🌍', desc: 'DigiKey, Mouser, Farnell – live stock & lead times' },
    { title: 'Orchestrator response', icon: '📝', desc: 'Orchestrator classifying parts – IGNORE / CRITICAL / CONSTRAINT' },
    { title: 'Completed', icon: '✅', desc: 'Batch worker finished – shortage insights ready' }
  ]

  // Derive current step and capture last log message per step for display
  const { currentStepIndex, stepMessages } = React.useMemo(() => {
    const messages: (string | null)[] = Array(STEPS.length).fill(null)
    let step = 0

    for (const evt of logLines) {
      const msg = evt.message
      const src = evt.source
      if (/Batch Worker Started/i.test(msg)) {
        step = Math.max(step, 1)
        messages[1] = msg
      }
      if (/status RUNNING|moved to processing/i.test(msg)) {
        step = Math.max(step, 2)
        messages[2] = msg
      }
      if (/get_mpns_from_aml: fetching AML/i.test(msg)) {
        step = Math.max(step, 3)
        messages[3] = msg
      }
      if (/JPNs found in AML/i.test(msg)) {
        step = Math.max(step, 4)
        messages[4] = msg
      }
      if (/Processing \d+ parts for job/i.test(msg)) {
        step = Math.max(step, 5)
        messages[5] = msg
      }
      if (src === 'orchestrator' && (/call_supplier_agent|get_jabil_inventory/i.test(msg))) {
        step = Math.max(step, 6)
        messages[6] = msg
      }
      if (src === 'supplier' && /SupplierAPIsLambda/i.test(msg)) {
        step = Math.max(step, 7)
        messages[7] = msg
      }
      if (src === 'orchestrator' && /OrchestratorResponse/i.test(msg)) {
        step = Math.max(step, 8)
        messages[8] = msg
      }
      if (/Job .+ completed:|Batch Worker Completed Successfully|status COMPLETED/i.test(msg)) {
        step = Math.max(step, 9)
        messages[9] = msg
      }
    }

    if (jobStatus === 'COMPLETED') step = STEPS.length - 1
    if (!jobToken) step = 0

    return { currentStepIndex: step, stepMessages: messages }
  }, [jobToken, jobStatus, logLines])

  // Live logs: SSE to drive steps (no log UI)
  useEffect(() => {
    seenEventIds.current = new Set()
    const es = new EventSource(LIVE_LOGS_URL)
    liveEventSourceRef.current = es

    es.onopen = () => {}

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as LiveConnected | LiveStatus | LiveLogEvent
        if (!data || typeof data !== 'object') return
        if ('type' in data && data.type === 'connected') {
          setLiveConnected(data as LiveConnected)
          return
        }
        if ('type' in data && data.type === 'status') return
        const logEvt = data as LiveLogEvent
        if (logEvt.source && typeof logEvt.message === 'string') {
          const id = logEvt.eventId ?? `${logEvt.timestamp ?? ''}-${logEvt.source}-${logEvt.message.slice(0, 50)}`
          if (seenEventIds.current.has(id)) return
          seenEventIds.current.add(id)
          setLogLines((prev) => [
            ...prev,
            { source: logEvt.source, message: logEvt.message, timestamp: logEvt.timestamp, eventId: logEvt.eventId },
          ])
        }
      } catch {
        // ignore
      }
    }

    es.onerror = () => {
      es.close()
      liveEventSourceRef.current = null
    }

    return () => {
      es.close()
      liveEventSourceRef.current = null
    }
  }, [])

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      if (validTypes.includes(droppedFile.type) || droppedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
        setFile(droppedFile)
        setMessage(null)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV or Excel file' })
        setFile(null)
      }
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      if (validTypes.includes(selectedFile.type) || selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx')) {
        setFile(selectedFile)
        setMessage(null)
      } else {
        setMessage({ type: 'error', text: 'Please select a CSV or Excel file' })
        setFile(null)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file' })
      return
    }

    setLoading(true)
    setMessage({ type: 'loading', text: 'Uploading file and starting job...' })

    try {
      const config = {
        part_limit: partLimit,
        email: {
          enabled: emailEnabled,
          recipient_emails: [...recipientEmails]
        }
      }
      const jobId = await onFileUpload(file, config)
      setMessage({ type: 'success', text: `Job submitted successfully! Job ID: ${jobId}` })
      onTokenReceived(jobId)
      setFile(null)
    } catch (error) {
      setMessage({ type: 'error', text: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}` })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="upload-tab-wrapper">
      {/* Left: form + status block with progress bar */}
      <div className="upload-tab-left">
        <div className="form-container">
          <h2>Run Job</h2>

          {/* Configuration Section */}
          <div className="configuration-section">
            <button
              type="button"
              className="config-toggle"
              onClick={() => setShowConfig(!showConfig)}
            >
              <span className="toggle-icon">{showConfig ? '▼' : '▶'}</span>
              Configuration
            </button>

            {showConfig && (
              <div className="config-content">
                <div className="form-group">
                  <label htmlFor="part-limit">Part Limit</label>
                  <input
                    id="part-limit"
                    type="number"
                    min={1}
                    value={partLimit}
                    onChange={(e) => setPartLimit(Number(e.target.value) || 5)}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={emailEnabled}
                      onChange={(e) => setEmailEnabled(e.target.checked)}
                    />
                    Email notifications enabled
                  </label>
                </div>

                {emailEnabled && (
                  <div className="form-group">
                    <label>Recipient emails</label>
                    <ul className="email-list">
                      {recipientEmails.map((email, i) => (
                        <li key={i} className="email-list-item">
                          <span>{email}</span>
                          <button
                            type="button"
                            className="email-remove"
                            onClick={() => setRecipientEmails((prev) => prev.filter((_, j) => j !== i))}
                            aria-label={`Remove ${email}`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="email-add">
                      <input
                        type="email"
                        placeholder="Add email (e.g. user@example.com)"
                        value={newEmailInput}
                        onChange={(e) => setNewEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            const v = newEmailInput.trim()
                            if (v && !recipientEmails.includes(v)) {
                              setRecipientEmails((prev) => [...prev, v])
                              setNewEmailInput('')
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const v = newEmailInput.trim()
                          if (v && !recipientEmails.includes(v)) {
                            setRecipientEmails((prev) => [...prev, v])
                            setNewEmailInput('')
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="file-input">Select or Drag and Drop CSV or Excel File</label>
              <div
                className={`file-drop-area ${dragActive ? 'active' : ''} ${file ? 'file-selected' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  id="file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={loading}
                  className="file-input-hidden"
                />
                <div className="file-drop-content">
                  <svg className="drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  <p>Drag and drop your file here or click to select</p>
                  <small>Supported formats: CSV, XLSX, XLS (Max 50 MB)</small>
                </div>
              </div>
            </div>

            {file && (
              <div className="form-group" style={{ color: '#667eea', fontWeight: 500 }}>
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={!file || loading}>
              {loading && <span className="spinner"></span>}
              {loading ? 'Uploading...' : 'Upload File'}
            </button>

            {message && (
              <div className={`status-message status-${message.type}`}>
                {message.text}
              </div>
            )}
          </form>

          {/* Status block: Job ID, progress bar, status, Show details when COMPLETED */}
          <div className="upload-status-block">
            {jobToken && (
              <div className="upload-status-inner">
                <div style={{ fontSize: '14px', color: '#333', marginBottom: 10 }}>
                  <strong>Job ID:</strong> {jobToken}
                </div>

                {jobTotalRows > 0 && (
                  <div className="job-progress-wrap" style={{ marginBottom: 12 }}>
                    <div className="job-progress-header">
                      <span className="job-progress-label">Progress</span>
                      <span className="job-progress-count">{jobProgress} / {jobTotalRows} parts</span>
                    </div>
                    <div className="job-progress-track">
                      <div
                        className="job-progress-fill"
                        style={{ width: `${Math.min(100, (jobProgress / jobTotalRows) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ fontSize: '14px', color: '#333', marginBottom: jobStatus === 'COMPLETED' ? 12 : 0 }}>
                  <strong>Status:</strong> <span style={{ fontWeight: 'bold', color: jobStatus === 'COMPLETED' ? 'rgb(87, 130, 43)' : '#667eea' }}>{jobStatus || 'Processing...'}</span>
                </div>

                {jobStatus === 'COMPLETED' && (
                  <button
                    type="button"
                    className="btn btn-primary btn-show-details"
                    onClick={() => navigate(`/job/${jobToken}`)}
                  >
                    Show details
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right: pipeline progress steps (disabled until file is uploaded) */}
      <div className={`upload-tab-right ${!jobToken ? 'upload-tab-right--disabled' : ''}`} aria-live="polite">
        <div className="upload-steps">
          <h4 className="upload-steps-title">Pipeline progress</h4>
          {!jobToken && (
            <p className="upload-steps-placeholder">Upload a file to see pipeline progress</p>
          )}
          {STEPS.map((step, i) => {
            const state = i < currentStepIndex ? 'done' : i === currentStepIndex ? 'current' : 'pending'
            const goodText = stepMessages[i] ? formatLogForStep(stepMessages[i]) : step.desc
            return (
              <div key={i} className={`upload-step upload-step--${state}`}>
                <div className="upload-step-marker">
                  {state === 'done' ? '✓' : step.icon}
                </div>
                <div className="upload-step-body">
                  <div className="upload-step-title">{step.title}</div>
                  <div className={`upload-step-desc ${stepMessages[i] ? 'upload-step-desc--log' : ''}`}>
                    {goodText}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default UploadTab
